import { useLayoutEffect, useRef, useState } from 'react';

// A dependency-free stacked column chart: one column per categorical x slot
// (months), one segment per series, stacked to the slot's total.
//
// This is the part-to-whole-over-time form, and it is the right one when the
// reader's question is "what is this month made of" — the column height carries the
// total and the segments carry the composition, so both are read in one pass. Lines
// answer a different question ("which service is trending"), which is why the
// console keeps a line chart alongside rather than replacing it.
//
// Every series must share a unit: the segments are summed into one column, so
// stacking USD onto SGD would produce a bar of nothing. The caller renders one
// chart per currency (see chart-multiples).
//
// It is drawn at the container's *measured* pixel width rather than scaled from a
// fixed viewBox, so the 11px axis type and the 2px separators stay true at any
// width instead of scaling with the container.
//
// props:
//   labels       x categories, oldest first
//   series       [{ key, label, values, color }] — values align 1:1 with labels;
//                null/undefined is nothing reported, 0 is a reported zero. color is
//                optional; without it the series takes a slot from the palette.
//   formatValue  value → string, for the tooltip
//   formatTick   value → string for the y ticks (defaults to formatValue)
//   formatLabel  (label, index) → short string for the x ticks
//   formatPoint  (label, index) → the tooltip's heading (defaults to formatLabel)
//   height       chart height in px
//   ariaLabel    what the chart shows, for a screen reader reaching the SVG

// The categorical slots, in assignment order — the same validated set the line
// chart uses. See global.css for what "validated" covers.
const SLOTS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];

// A slot per series *key*, never per position: a service that overtakes another in
// the ranking, or drops out of a filtered month, must not repaint the rest. Series
// that name their own colour (an "Other" fold) are left out of the draw.
const slotsFor = (series) => {
  const taken = new Array(SLOTS.length).fill(false);
  const out = new Map();
  for (const s of series) {
    if (s.color) {
      out.set(s.key, s.color);
      continue;
    }
    const key = String(s.key);
    let slot = 0;
    for (let i = 0; i < key.length; i += 1) slot = (slot + key.charCodeAt(i)) % SLOTS.length;
    while (taken[slot]) slot = (slot + 1) % SLOTS.length;
    taken[slot] = true;
    out.set(s.key, SLOTS[slot]);
  }
  return out;
};

// Round tick values, so the axis reads 0 / 500 / 1,000 rather than 0 / 437 / 874.
const niceMax = (max, ticks) => {
  if (!(max > 0)) return 1;
  const rough = max / ticks;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / magnitude;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * magnitude;
  return step * ticks;
};

const useMeasuredWidth = (fallback) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(Math.round(el.clientWidth), 320));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

const TICKS = 4;
const PAD = { top: 18, right: 20, bottom: 28, left: 66 };
// A column takes this much of its slot; the leftover is the air that keeps twelve
// months from reading as one solid block. Width is therefore a function of how many
// columns there are — three months in a wide card get wide columns, sixty days get
// narrow ones — because the slot is what the reader is comparing within.
const BAR_RATIO = 0.6;
// ...capped, so a three-column chart is three columns and not three walls. The cap is
// generous: the failure it exists to prevent (a slab so wide the eye reads area
// rather than height) is far rarer than the one a tight cap causes — a 24px column
// centred in a 500px slot, which reads as a hairline chart in a mostly empty card.
const MAX_BAR = 120;
// The gap that separates touching segments, in the surface colour. White does the
// separating — never a stroke around each segment, which would add ink that is not
// data.
const GAP = 2;
// The narrowest slot a column may be given. Below it the chart stops shrinking and
// starts widening instead, scrolling inside `.chart__scroll` — sixty days squeezed
// into a card leaves every column a hairline and drops most of the x labels, which
// reads as a chart of nothing rather than as a chart that does not fit. 44px keeps a
// column around 26px with air either side, and is roughly what a date tick needs to
// stay legible.
const MIN_BAND = 44;

export default function StackedBars({
  labels = [],
  series = [],
  formatValue = (v) => String(v),
  formatTick,
  formatLabel = (label) => label,
  formatPoint,
  height = 260,
  ariaLabel = 'Stacked bar chart',
  emptyText = 'No data',
}) {
  const [box, available] = useMeasuredWidth(720);
  // Which (label index, series key) the pointer is on. On bars the mark is the hit
  // target — there is no crosshair, because the reader aims at a segment.
  const [active, setActive] = useState(null);

  const count = labels.length;
  const tick = formatTick || formatValue;
  const point = formatPoint || formatLabel;
  // The chart is drawn at whichever is wider: the space it has, or the space its
  // columns need. Past that point the plot overflows and `.chart__scroll` scrolls
  // it sideways, so a month keeps the same column width whether there are three of
  // them or sixty days of them — the reader compares heights, and a column that
  // changes width between views is comparing two things at once.
  const width = Math.max(available, PAD.left + PAD.right + count * MIN_BAND);
  const plotW = Math.max(width - PAD.left - PAD.right, 10);
  const plotH = Math.max(height - PAD.top - PAD.bottom, 10);

  // The axis is scaled to the tallest column, not to the tallest segment: the
  // column is what the reader compares.
  const totals = labels.map((_, i) =>
    series.reduce((sum, s) => {
      const v = (s.values || [])[i];
      return sum + (v == null || Number.isNaN(v) ? 0 : v);
    }, 0)
  );
  const top = niceMax(Math.max(0, ...totals), TICKS);

  if (count === 0 || series.length === 0) {
    return <div className="chart chart--empty">{emptyText}</div>;
  }

  const colors = slotsFor(series);
  const ticks = Array.from({ length: TICKS + 1 }, (_, i) => (top / TICKS) * i);
  const band = plotW / count;
  const barW = Math.min(MAX_BAR, band * BAR_RATIO);
  // Centre of each slot, so a column sits in the middle of its own band rather
  // than on the tick.
  const x = (i) => PAD.left + band * i + band / 2;
  const y = (v) => PAD.top + plotH * (1 - v / top);
  const labelStep = Math.max(1, Math.ceil(count / Math.max(1, Math.floor(plotW / 64))));

  // Segment geometry per column, bottom-up. Built once so the tooltip and the marks
  // agree on what is where.
  const columns = labels.map((label, i) => {
    let base = 0;
    const parts = [];
    for (const s of series) {
      const raw = (s.values || [])[i];
      const value = raw == null || Number.isNaN(raw) ? null : raw;
      if (value == null || value <= 0) {
        // A zero or absent segment gets no rectangle: a 0px-tall bar with a 2px gap
        // above it would draw as a stripe of nothing.
        parts.push({ key: s.key, label: s.label ?? s.key, value, rect: null });
        continue;
      }
      const yTop = y(base + value);
      const yBottom = y(base);
      // The gap is taken off the top of each segment, so it never eats the
      // baseline and never makes a segment taller than its value.
      const h = Math.max(yBottom - yTop - GAP, 1);
      parts.push({
        key: s.key,
        label: s.label ?? s.key,
        value,
        rect: { x: x(i) - barW / 2, y: yTop + GAP, width: barW, height: h },
      });
      base += value;
    }
    return { label, index: i, total: base, parts };
  });

  const activeColumn = active == null ? null : columns[active.index];
  const activePart = activeColumn?.parts.find((p) => p.key === active.key) || null;
  // Anchored on the hovered column, pulled inside the plot at either edge.
  const tipX = activeColumn == null ? 0 : x(activeColumn.index);
  const tipAnchor =
    tipX > width - 130
      ? 'translateX(-100%)'
      : tipX < PAD.left + 130
        ? 'translateX(0)'
        : 'translateX(-50%)';

  return (
    <div className="chart" ref={box}>
      {/* The plot scrolls sideways when it is wider than the card; the legend
          stays outside the scroller so it cannot slide away from the chart it
          names. The tooltip is inside, because it is positioned in the plot's
          own coordinates and has to travel with it. */}
      <div className="chart__scroll">
        <svg
          className="chart__svg"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          // A group, not an img: the segments below are focusable, and `img` tells a
          // screen reader to treat everything inside as presentational — which would
          // hide the very values keyboard focus is there to reach.
          role="group"
          aria-label={ariaLabel}
          onPointerLeave={() => setActive(null)}
        >
          <g className="chart__grid">
            {ticks.map((t) => (
              <line key={t} x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} />
            ))}
          </g>
          <g className="chart__axis">
            {ticks.map((t) => (
              <text key={t} x={PAD.left - 10} y={y(t)} textAnchor="end" dominantBaseline="middle">
                {tick(t)}
              </text>
            ))}
            {labels.map((label, i) =>
              i % labelStep === 0 || i === count - 1 ? (
                <text key={label} x={x(i)} y={height - 8} textAnchor="middle">
                  {formatLabel(label, i)}
                </text>
              ) : null
            )}
          </g>

          {columns.map((column) =>
            column.parts.map((part) =>
              part.rect ? (
                <rect
                  key={`${column.label}-${part.key}`}
                  className={`chart__seg ${
                    active && active.index === column.index && active.key === part.key
                      ? 'chart__seg--on'
                      : ''
                  }`}
                  x={part.rect.x}
                  y={part.rect.y}
                  width={part.rect.width}
                  height={part.rect.height}
                  fill={colors.get(part.key)}
                  // Square: no component in this console carries a corner radius, so
                  // the 2px surface gaps do all of the separating.
                  tabIndex={0}
                  role="button"
                  aria-label={`${part.label}, ${point(column.label, column.index)}, ${formatValue(
                    part.value
                  )}`}
                  onPointerEnter={() => setActive({ index: column.index, key: part.key })}
                  onPointerMove={() => setActive({ index: column.index, key: part.key })}
                  onFocus={() => setActive({ index: column.index, key: part.key })}
                  onBlur={() => setActive(null)}
                />
              ) : null
            )
          )}
        </svg>

        {activePart ? (
          <div className="chart__tip" role="status" style={{ left: tipX, transform: tipAnchor }}>
            <div className="chart__tip-when">{point(activeColumn.label, activeColumn.index)}</div>
            <div className="chart__tip-row">
              <span className="chart__key" style={{ background: colors.get(activePart.key) }} />
              <strong className="chart__tip-value">
                {activePart.value == null ? 'no data' : formatValue(activePart.value)}
              </strong>
              <span className="chart__tip-label">{activePart.label}</span>
            </div>
            {/* The column total, because a segment is only readable against the whole
                it is part of. */}
            <div className="chart__tip-total">
              {formatValue(activeColumn.total)} total
              {activeColumn.total > 0
                ? ` · ${Math.round((activePart.value / activeColumn.total) * 100)}%`
                : ''}
            </div>
          </div>
        ) : null}
      </div>

      <ul className="chart__legend">
        {series.map((s) => (
          <li key={s.key} className="chart__legend-item">
            <span className="chart__swatch" style={{ background: colors.get(s.key) }} />
            {s.label ?? s.key}
          </li>
        ))}
      </ul>
    </div>
  );
}
